import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Button, Badge } from '@/components/atoms';
import { Input, Card } from '@kisanlink/ui-commons';
import { Modal, Breadcrumb } from '@/components/molecules';
import { ProductForm, ProductVariantForm } from '@/components/organisms';
import { VariantViewModal } from '@/components/organisms/ProductVariant/VariantViewModal';
import { BatchForm } from '@/components/organisms/Batch';
import {
  useProductsList,
  useProductAvailability,
  useDeleteProductMutation,
  useCategoriesList,
  useSubcategoriesList,
  useSubcategoriesByCategory,
  useProductsByCategoryQuery,
  useProductsByQuantity,
} from '@/hooks/useErpApi';
import type {
  ProductResponse,
  ProductAvailabilityResponse,
  ProductVariantResponse,
} from '@kisanlink/erp-service';

interface ExpandedState {
  products: Set<string>;
  variants: Set<string>;
}

type ViewMode = 'categories' | 'subcategories' | 'products';

/**
 * ProductInventoryPage - Optimized for minimal API calls
 *
 * OPTIMIZATION STRATEGY:
 * 1. Products list: Fetched ONCE with 5min cache - includes preloaded variants
 * 2. Availability: Fetched ONCE with 2min cache (changes with GRN/sales)
 * 3. Variants: Preloaded with products - no separate API calls needed
 *    - Uses image_urls field with presigned URLs for direct image display
 *
 * PERFORMANCE METRICS:
 * - Initial load: 2 API calls (products with variants + availability)
 * - No additional API calls for variants or images
 *
 * CACHE TIMES (as per spec):
 * - Products: 5min stale, 10min cache
 * - Availability: 2min stale, 5min cache
 */
// Helper function to get icon name based on category name
const getCategoryIcon = (categoryName: string): string => {
  const name = categoryName.toUpperCase();
  if (name.includes('FERTILIZER') || name.includes('FERTILISER')) {
    return 'Package'; // Use Package for fertilizers
  }
  if (name.includes('SEED')) {
    return 'Package'; // Use Package for seeds
  }
  if (name.includes('PESTICIDE')) {
    return 'Shield';
  }
  if (name.includes('IRRIGATION') || name.includes('WATER')) {
    return 'Droplet';
  }
  if (name.includes('IMPLEMENT') || name.includes('TOOL')) {
    return 'Wrench';
  }
  if (name.includes('BIO') || name.includes('ORGANIC')) {
    return 'Package'; // Use Package for bio products
  }
  if (name.includes('MACHINERY') || name.includes('EQUIPMENT')) {
    return 'Settings';
  }
  return 'Package'; // Default icon
};

export default function ProductInventoryPage() {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>('categories');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [quantityFilter, setQuantityFilter] = useState<'all' | 'out-of-stock' | 'low-stock' | 'optimal' | 'overstocked'>('all');
  const [expanded, setExpanded] = useState<ExpandedState>({
    products: new Set(),
    variants: new Set(),
  });
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddVariantModalOpen, setIsAddVariantModalOpen] = useState(false);
  const [isAddBatchModalOpen, setIsAddBatchModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductResponse | null>(null);
  const [selectedProductForVariant, setSelectedProductForVariant] =
    useState<ProductResponse | null>(null);
  const [selectedVariantForBatch, setSelectedVariantForBatch] =
    useState<ProductVariantResponse | null>(null);
  const [selectedVariantForView, setSelectedVariantForView] =
    useState<ProductVariantResponse | null>(null);
  const [selectedVariantForEdit, setSelectedVariantForEdit] =
    useState<ProductVariantResponse | null>(null);
  const [isVariantViewModalOpen, setIsVariantViewModalOpen] = useState(false);
  const [isVariantEditModalOpen, setIsVariantEditModalOpen] = useState(false);

  // Quantity filter ranges
  const quantityFilterRanges = {
    'all': null,
    'out-of-stock': { min: 0, max: 0 },
    'low-stock': { min: 1, max: 10 },
    'optimal': { min: 11, max: 100 },
    'overstocked': { min: 101, max: 999999 },
  };

  // Fetch products by quantity if filter is active (not 'all')
  const quantityFilterRange = quantityFilterRanges[quantityFilter];
  
  // Determine params for quantity API - use actual range when filter is active
  const quantityParams = quantityFilter !== 'all' && quantityFilterRange 
    ? quantityFilterRange 
    : { min: -1, max: -1 }; // Disabled params when filter is 'all'
  
  console.log('[ProductInventoryPage] Quantity filter state:', {
    quantityFilter,
    quantityFilterRange,
    quantityParams,
    willCallAPI: quantityFilter !== 'all' && quantityFilterRange !== null
  });
  
  // Only call the hook when filter is active, otherwise pass disabled params
  const { data: quantityFilteredProducts, isLoading: quantityFilterLoading } = useProductsByQuantity(quantityParams);
  
  // Debug: Log when quantity filter changes
  useEffect(() => {
    console.log('[ProductInventoryPage] Quantity filter changed:', {
      quantityFilter,
      quantityFilterRange,
      quantityParams,
      hasData: !!quantityFilteredProducts,
      isLoading: quantityFilterLoading
    });
  }, [quantityFilter, quantityFilterRange, quantityParams, quantityFilteredProducts, quantityFilterLoading]);

  // Fetch products based on view mode - ONLY when quantity filter is 'all'
  // Disable these queries when quantity filter is active
  const {
    data: allProducts,
    isLoading: productsLoading,
    error: productsError,
    refetch,
  } = useProductsList();

  // Fetch products by category when viewing category/subcategory products - ONLY when quantity filter is 'all'
  // CRITICAL: Disable category query when quantity filter is active - we use quantity API instead
  const shouldFetchCategoryProducts = quantityFilter === 'all' && viewMode === 'products' && !!selectedCategoryId;
  const {
    data: categoryProductsData,
    isLoading: categoryProductsLoading,
  } = useProductsByCategoryQuery(
    selectedCategoryId || '',
    selectedSubcategoryId ? { subcategory_id: selectedSubcategoryId } : undefined,
    { enabled: shouldFetchCategoryProducts } // Disable when quantity filter is active
  );

  // Determine which products to use based on view mode and quantity filter
  const products = useMemo(() => {
    // If quantity filter is active, use ONLY the quantity-filtered products
    if (quantityFilter !== 'all' && quantityFilteredProducts) {
      const filteredData = Array.isArray(quantityFilteredProducts) 
        ? quantityFilteredProducts 
        : (quantityFilteredProducts as any)?.data || [];
      console.log('[ProductInventoryPage] Using quantity-filtered products:', filteredData.length, 'products');
      return filteredData;
    }
    
    if (quantityFilter !== 'all') {
      console.log('[ProductInventoryPage] Quantity filter active but no data yet, loading:', quantityFilterLoading);
    }

    // Otherwise, use normal product fetching logic
    if (viewMode === 'products' && selectedCategoryId) {
      if (categoryProductsData) {
        if (Array.isArray(categoryProductsData)) {
          return categoryProductsData;
        }
        if ((categoryProductsData as any)?.data && Array.isArray((categoryProductsData as any).data)) {
          return (categoryProductsData as any).data;
        }
      }
      return [];
    }
    return allProducts || [];
  }, [viewMode, selectedCategoryId, categoryProductsData, allProducts, quantityFilter, quantityFilteredProducts]);

  // Loading state: use quantity filter loading when filter is active, otherwise use normal loading
  const isLoading = quantityFilter !== 'all' 
    ? quantityFilterLoading 
    : (productsLoading || (viewMode === 'products' && categoryProductsLoading));

  // Fetch availability ONCE with proper caching (2min stale, 5min cache as per spec)
  const { data: availability, isLoading: availabilityLoading } = useProductAvailability();
  const { mutate: deleteProduct } = useDeleteProductMutation();
  
  // Fetch categories and subcategories for name lookup
  const { data: categoriesData } = useCategoriesList();
  const { data: subcategoriesData } = useSubcategoriesList();
  
  // Fetch subcategories for selected category - ONLY when quantity filter is 'all' and category is selected
  // CRITICAL: Disable subcategories API when quantity filter is active - they are separate filters
  // Also fetch when in products view with a selected category (for subcategory filter)
  const shouldFetchSubcategories: boolean = quantityFilter === 'all' && !!selectedCategoryId && (viewMode === 'products' || viewMode === 'subcategories');
  const { data: categorySubcategoriesData } = useSubcategoriesByCategory(
    selectedCategoryId || '',
    { enabled: shouldFetchSubcategories } // Disable when quantity filter is active
  );

  const isLoadingProducts = isLoading || availabilityLoading;
  
  // Extract categories and subcategories from API responses
  const categories = useMemo(() => {
    if (!categoriesData) return [];
    if (Array.isArray(categoriesData)) {
      return categoriesData;
    }
    if ((categoriesData as any)?.data && Array.isArray((categoriesData as any).data)) {
      return (categoriesData as any).data;
    }
    return [];
  }, [categoriesData]);
  
  const subcategories = React.useMemo(() => {
    if (!subcategoriesData) return [];
    if (Array.isArray(subcategoriesData)) {
      return subcategoriesData;
    }
    if ((subcategoriesData as any)?.data && Array.isArray((subcategoriesData as any).data)) {
      return (subcategoriesData as any).data;
    }
    return [];
  }, [subcategoriesData]);

  // Get subcategories for selected category - used for both subcategories view and products view filter
  const categorySubcategories = useMemo(() => {
    if (!selectedCategoryId) return [];
    if (!categorySubcategoriesData) return [];
    if (Array.isArray(categorySubcategoriesData)) {
      return categorySubcategoriesData;
    }
    if ((categorySubcategoriesData as any)?.data && Array.isArray((categorySubcategoriesData as any).data)) {
      return (categorySubcategoriesData as any).data;
    }
    return [];
  }, [selectedCategoryId, categorySubcategoriesData]);
  
  // Create maps for quick lookup of category/subcategory names by ID
  const categoryMap = useMemo(() => {
    const map = new Map<string, string>();
    categories.forEach((cat: any) => {
      map.set(cat.id, cat.name);
    });
    return map;
  }, [categories]);
  
  const subcategoryMap = useMemo(() => {
    const map = new Map<string, string>();
    subcategories.forEach((subcat: any) => {
      map.set(subcat.id, subcat.name);
    });
    return map;
  }, [subcategories]);

  // Group availability data by variant_id (new grouped structure)
  const availabilityMap = useMemo(() => {
    const map = new Map<string, ProductAvailabilityResponse>();
    (availability || []).forEach((item: ProductAvailabilityResponse) => {
      // New structure: items are already grouped by SKU, key by variant_id
      const key = (item as any).variant_id;
      if (key) {
        map.set(key, item);
      }
    });
    return map;
  }, [availability]);

  const toggleProduct = (productId: string) => {
    setExpanded((prev) => {
      const newProducts = new Set(prev.products);
      if (newProducts.has(productId)) {
        newProducts.delete(productId);
      } else {
        newProducts.add(productId);
      }
      return { ...prev, products: newProducts };
    });
  };

  const toggleVariant = (variantId: string) => {
    setExpanded((prev) => {
      const newVariants = new Set(prev.variants);
      if (newVariants.has(variantId)) {
        newVariants.delete(variantId);
      } else {
        newVariants.add(variantId);
      }
      return { ...prev, variants: newVariants };
    });
  };

  const filteredProducts = (products || []).filter((product: ProductResponse) =>
    product.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Navigation handlers
  const handleCategoryClick = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
    setSelectedSubcategoryId(null);
    setViewMode('products'); // Go directly to products, not subcategories
  };

  const handleViewAllProducts = () => {
    setSelectedCategoryId(null);
    setSelectedSubcategoryId(null);
    setViewMode('products');
  };

  const handleExploreAll = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
    setSelectedSubcategoryId(null);
    setViewMode('products');
  };

  const handleSubcategoryClick = (categoryId: string, subcategoryId: string) => {
    setSelectedCategoryId(categoryId);
    setSelectedSubcategoryId(subcategoryId);
    setViewMode('products');
  };

  const handleBackToCategories = () => {
    setViewMode('categories');
    setSelectedCategoryId(null);
    setSelectedSubcategoryId(null);
  };


  // Build breadcrumb items based on current view - always start from Home
  const breadcrumbItems = useMemo(() => {
    const items: Array<{ label: string; onClick?: () => void }> = [];

    // Always show "Home" as first item (clickable to go back to categories)
    items.push({
      label: 'Home',
      onClick: handleBackToCategories,
    });

    // Add category if selected (clickable to go back to categories)
    if (selectedCategoryId) {
      const categoryName = categoryMap.get(selectedCategoryId) || 'Category';
      items.push({
        label: categoryName,
        onClick: handleBackToCategories, // Clicking category goes back to categories view
      });
    }

    // Add subcategory if selected (clickable to remove subcategory filter)
    if (selectedSubcategoryId) {
      items.push({
        label: subcategoryMap.get(selectedSubcategoryId) || 'Subcategory',
        onClick: () => {
          setSelectedSubcategoryId(null);
          // Stay in products view, just remove subcategory filter
        },
      });
    }

    // If in products view, show "Products" as current page
    if (viewMode === 'products') {
      if (!selectedCategoryId) {
        items.push({
          label: 'All Products',
        });
      } else {
        items.push({
          label: 'Products',
        });
      }
    }

    return items;
  }, [viewMode, selectedCategoryId, selectedSubcategoryId, categoryMap, subcategoryMap, handleBackToCategories]);

  const handleFormSuccess = () => {
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    setSelectedProduct(null);
    refetch();
  };

  const handleVariantFormSuccess = () => {
    setIsAddVariantModalOpen(false);
    setSelectedProductForVariant(null);
    // Refetch products to get updated variants
    refetch();
  };

  const handleBatchFormSuccess = () => {
    setIsAddBatchModalOpen(false);
    setSelectedVariantForBatch(null);
    refetch();
  };

  const handleDeleteProduct = async (productId: string) => {
    if (window.confirm('Are you sure you want to delete this product and all its variants?')) {
      try {
        await deleteProduct(productId);
        refetch();
      } catch (err) {
        // Error handled by mutation
      }
    }
  };

  // Helper function to get total stock for a product (sum across all variants)
  const getTotalStock = (product: ProductResponse) => {
    if (!product.variants) return 0;
    return product.variants.reduce((sum, variant) => {
      const availabilityItem = availabilityMap.get(variant.id);
      if (availabilityItem) {
        // New structure: total_quantity is already aggregated
        return sum + ((availabilityItem as any).total_quantity || 0);
      }
      return sum;
    }, 0);
  };

  // Helper function to get stock for a specific variant
  const getVariantStock = (variantId: string) => {
    const availabilityItem = availabilityMap.get(variantId);
    if (availabilityItem) {
      return ((availabilityItem as any).total_quantity || 0);
    }
    return 0;
  };

  // Helper function to get stock status
  const getStockStatus = (
    quantity: number
  ): { variant: 'success' | 'warning' | 'error'; label: string } => {
    if (quantity === 0) return { variant: 'error', label: 'Out of Stock' };
    if (quantity < 10) return { variant: 'warning', label: 'Low Stock' };
    return { variant: 'success', label: 'In Stock' };
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (isLoadingProducts && viewMode === 'products') {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <Icon name="Package" size={48} className="text-neutral-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-neutral-600">Loading product inventory...</p>
        </div>
      </div>
    );
  }

  if (productsError) {
    return (
      <div className="p-8">
        <div className="bg-error-50 border border-error-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="bg-error-100 p-2 rounded-lg">
              <Icon name="TriangleAlert" size={24} className="text-error-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-error-900 mb-1">Failed to Load Data</h3>
              <p className="text-error-700 mb-3">
                Unable to fetch product inventory. Please try again.
              </p>
              <Button variant="primary" size="sm" onClick={() => refetch()}>
                <Icon name="RefreshCw" size={16} className="mr-2" />
                Retry
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const totalProducts = products?.length || 0;
  // Count total variants from all preloaded product variants
  const totalVariants = (products || []).reduce(
    (sum: number, product: ProductResponse) => sum + (product.variants?.length || 0),
    0
  );
  // Total batches = number of SKU groups (new structure groups by SKU)
  const totalBatches = availability?.length || 0;
  // Total stock value calculation - cost_price not available in grouped structure
  // Would need to fetch from batches or variants separately
  const totalStockValue = 0; // TODO: Calculate from batch data if needed

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-neutral-900">Product Inventory</h1>
          <p className="text-neutral-600 mt-2">
            Manage products, variants, and stock across warehouses
          </p>
        </div>
        <Button variant="primary" onClick={() => setIsAddModalOpen(true)}>
          <Icon name="Plus" size={20} className="mr-2" />
          Add Product
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-neutral-600">Products</p>
            <div className="bg-primary-50 p-2 rounded-lg">
              <Icon name="Package" size={20} className="text-primary-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-neutral-900">{totalProducts}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-neutral-600">Variants</p>
            <div className="bg-info-50 p-2 rounded-lg">
              <Icon name="Layers" size={20} className="text-info-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-neutral-900">{totalVariants}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-neutral-600">Batches</p>
            <div className="bg-success-50 p-2 rounded-lg">
              <Icon name="Warehouse" size={20} className="text-success-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-neutral-900">{totalBatches}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-neutral-600">Stock Value</p>
            <div className="bg-warning-50 p-2 rounded-lg">
              <Icon name="IndianRupee" size={20} className="text-warning-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-neutral-900">{formatCurrency(totalStockValue)}</p>
        </div>
      </div>

      {/* Breadcrumb Navigation - Always show from home */}
      {viewMode !== 'categories' && (
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4">
          <Breadcrumb items={breadcrumbItems} separator="chevron" />
        </div>
      )}

      {/* Search and Quantity Filters - only show in products view */}
      {viewMode === 'products' && (
        <div className="space-y-4">
          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4">
            <Input
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              leftIcon={<Icon name="Search" size={20} className="text-neutral-400" />}
            />
          </div>

          {/* Subcategory Filter - only show when a category is selected */}
          {selectedCategoryId && categorySubcategories.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Icon name="SlidersHorizontal" size={18} className="text-neutral-600" />
                <span className="text-sm font-medium text-neutral-700">Filter by Subcategory:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedSubcategoryId(null)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    !selectedSubcategoryId
                      ? 'bg-primary-600 text-white'
                      : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                  }`}
                >
                  All Subcategories
                </button>
                {categorySubcategories.map((subcategory: { id: string; name: string }) => (
                  <button
                    key={subcategory.id}
                    onClick={() => setSelectedSubcategoryId(subcategory.id)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedSubcategoryId === subcategory.id
                        ? 'bg-primary-600 text-white'
                        : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                    }`}
                  >
                    {subcategory.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Quantity Filter Buttons */}
          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="Settings" size={18} className="text-neutral-600" />
              <span className="text-sm font-medium text-neutral-700">Filter by Stock Level:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setQuantityFilter('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  quantityFilter === 'all'
                    ? 'bg-primary-600 text-white'
                    : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                }`}
              >
                All Products
              </button>
              <button
                onClick={() => setQuantityFilter('out-of-stock')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  quantityFilter === 'out-of-stock'
                    ? 'bg-error-600 text-white'
                    : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                }`}
              >
                Out of Stock (0)
              </button>
              <button
                onClick={() => setQuantityFilter('low-stock')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  quantityFilter === 'low-stock'
                    ? 'bg-warning-600 text-white'
                    : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                }`}
              >
                Low Stock (1-10)
              </button>
              <button
                onClick={() => setQuantityFilter('optimal')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  quantityFilter === 'optimal'
                    ? 'bg-success-600 text-white'
                    : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                }`}
              >
                Optimal (11-100)
              </button>
              <button
                onClick={() => setQuantityFilter('overstocked')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  quantityFilter === 'overstocked'
                    ? 'bg-info-600 text-white'
                    : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                }`}
              >
                Overstocked (100+)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Categories View - Show all category cards */}
      {viewMode === 'categories' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {/* View All Products Card - First Card */}
          <Card
            variant="elevated"
            clickable
            className="bg-gradient-to-br from-primary-50 to-primary-100 border-2 border-primary-300 shadow-md hover:shadow-lg transition-all cursor-pointer hover:border-primary-400"
            onClick={handleViewAllProducts}
          >
            <Card.Body className="p-6 bg-transparent">
              <div className="flex items-start justify-between mb-4">
                <div className="bg-primary-500 p-3 rounded-lg">
                  <Icon name="Package" size={24} className="text-white" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                View All Products
              </h3>
              <p className="text-sm text-neutral-600 mb-4">
                Browse all products across all categories
              </p>
              <div className="flex gap-2 mt-4">
                <Button
                  variant="primary"
                  size="sm"
                  className="flex-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewAllProducts();
                  }}
                >
                  <Icon name="ChevronRight" size={16} className="mr-1" />
                  View All
                </Button>
              </div>
            </Card.Body>
          </Card>

          {categories.map((category: { id: string; name: string; description?: string; subcategories?: Array<{ id: string; name: string }> }) => {
            const subcategoryCount = category.subcategories?.length || 0;
            const iconName = getCategoryIcon(category.name);
            return (
              <Card
                key={category.id}
                variant="elevated"
                clickable
                className="bg-white border-2 border-neutral-200 shadow-md hover:shadow-lg transition-all cursor-pointer hover:border-primary-300"
                onClick={() => handleCategoryClick(category.id)}
              >
                <Card.Body className="p-6 bg-white">
                  <div className="flex items-start justify-between mb-4">
                    <div className="bg-primary-50 p-3 rounded-lg">
                      <Icon name={iconName as any} size={24} className="text-primary-600" />
                    </div>
                    {subcategoryCount > 0 && (
                      <Badge variant="info" size="sm">
                        {subcategoryCount} {subcategoryCount === 1 ? 'subcategory' : 'subcategories'}
                      </Badge>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                    {category.name}
                  </h3>
                  {category.description && (
                    <p className="text-sm text-neutral-600 mb-4 line-clamp-2">
                      {category.description}
                    </p>
                  )}
                  <div className="flex gap-2 mt-4">
                    <Button
                      variant="primary"
                      size="sm"
                      className="flex-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCategoryClick(category.id);
                      }}
                    >
                      <Icon name="ChevronRight" size={16} className="mr-1" />
                      View
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExploreAll(category.id);
                      }}
                    >
                      Explore All
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            );
          })}
        </div>
      )}

      {/* Subcategories View */}
      {viewMode === 'subcategories' && selectedCategoryId && (
        <div>
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-neutral-900 mb-2">
              {categoryMap.get(selectedCategoryId) || 'Category'}
            </h2>
            <p className="text-neutral-600">Select a subcategory to view products</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-6">
            {categorySubcategories.map((subcategory: { id: string; name: string; description?: string }) => (
              <Card
                key={subcategory.id}
                variant="elevated"
                clickable
                className="bg-white border border-neutral-200 shadow-md hover:shadow-lg transition-all cursor-pointer hover:border-primary-300"
                onClick={() => handleSubcategoryClick(selectedCategoryId, subcategory.id)}
              >
                <Card.Body className="p-6 bg-white">
                  <div className="bg-info-50 p-3 rounded-lg mb-4 w-fit">
                    <Icon name="Layers" size={24} className="text-info-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                    {subcategory.name}
                  </h3>
                  {subcategory.description && (
                    <p className="text-sm text-neutral-600 mb-4 line-clamp-2">
                      {subcategory.description}
                    </p>
                  )}
                  <Button
                    variant="primary"
                    size="sm"
                    className="w-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSubcategoryClick(selectedCategoryId, subcategory.id);
                    }}
                  >
                    <Icon name="ChevronRight" size={16} className="mr-1" />
                    View Products
                  </Button>
                </Card.Body>
              </Card>
            ))}
          </div>
          <div className="mt-6">
            <Card variant="outlined" clickable className="bg-white border-2 border-neutral-300 shadow-sm hover:shadow-md hover:border-primary-400 transition-all">
              <Card.Body className="p-6 bg-white">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 mb-1">
                      Explore All Products
                    </h3>
                    <p className="text-sm text-neutral-600">
                      View all products in this category
                    </p>
                  </div>
                  <Button
                    variant="primary"
                    onClick={() => handleExploreAll(selectedCategoryId)}
                  >
                    <Icon name="ArrowRight" size={16} className="mr-2" />
                    Explore All
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </div>
        </div>
      )}

      {/* Products List */}
      {viewMode === 'products' && (
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-semibold text-neutral-700 uppercase">
                  Product
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-neutral-700 uppercase w-32">
                  Category
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-neutral-700 uppercase w-32">
                  Subcategory
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-neutral-700 uppercase w-20">
                  Variants
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-neutral-700 uppercase w-24">
                  Stock
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-neutral-700 uppercase w-24">
                  Status
                </th>
                <th className="px-3 py-3 text-right text-xs font-semibold text-neutral-700 uppercase w-28">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {filteredProducts.map((product: ProductResponse) => {
                const isProductExpanded = expanded.products.has(product.id);
                const variants = product.variants || [];
                const totalStock = getTotalStock(product);
                const stockStatus = getStockStatus(totalStock);

                return (
                  <React.Fragment key={product.id}>
                    {/* Product Row */}
                    <tr className="hover:bg-neutral-50">
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => toggleProduct(product.id)}
                            className="p-1 hover:bg-neutral-200 rounded transition-colors flex-shrink-0"
                          >
                            <Icon
                              name={isProductExpanded ? 'ChevronDown' : 'ChevronRight'}
                              size={14}
                              className="text-neutral-600"
                            />
                          </button>
                          {/* Product Thumbnail - show first variant's first image if available */}
                          {variants.length > 0 &&
                          variants[0].image_urls &&
                          variants[0].image_urls.length > 0 ? (
                            <img
                              src={variants[0].image_urls[0]}
                              alt={product.name}
                              className="h-10 w-10 flex-shrink-0 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="h-10 w-10 flex-shrink-0 bg-primary-100 rounded-lg flex items-center justify-center">
                              <Icon name="Package" size={16} className="text-primary-600" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-neutral-900 truncate">
                              {product.name}
                            </div>
                            {product.description && (
                              <div 
                                className="text-xs text-neutral-500 truncate max-w-[200px]" 
                                title={product.description}
                              >
                                {product.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="text-sm text-neutral-900 truncate" title={product.category_id ? (categoryMap.get(product.category_id) || '') : ''}>
                          {product.category_id ? (
                            categoryMap.get(product.category_id) || (
                              <span className="text-neutral-400 italic text-xs">Loading...</span>
                            )
                          ) : (
                            <span className="text-neutral-400">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="text-sm text-neutral-900 truncate" title={product.subcategory_id ? (subcategoryMap.get(product.subcategory_id) || '') : ''}>
                          {product.subcategory_id ? (
                            subcategoryMap.get(product.subcategory_id) || (
                              <span className="text-neutral-400 italic text-xs">Loading...</span>
                            )
                          ) : (
                            <span className="text-neutral-400">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="text-sm text-neutral-900 text-center">{variants.length}</div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="text-sm font-medium text-neutral-900">{totalStock}</div>
                      </td>
                      <td className="px-3 py-3">
                        <Badge variant={stockStatus.variant} size="sm">{stockStatus.label}</Badge>
                      </td>
                      <td className="px-3 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => navigate(`/product-inventory/${product.id}`)}
                            className="text-info-600 hover:text-info-900 p-1.5 rounded hover:bg-info-50"
                            title="View Product Details"
                          >
                            <Icon name="Eye" size={14} />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedProductForVariant(product);
                              setIsAddVariantModalOpen(true);
                            }}
                            className="text-success-600 hover:text-success-900 p-1.5 rounded hover:bg-success-50"
                            title="Add Variant"
                          >
                            <Icon name="Plus" size={14} />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedProduct(product);
                              setIsEditModalOpen(true);
                            }}
                            className="text-primary-600 hover:text-primary-900 p-1.5 rounded hover:bg-primary-50"
                            title="Edit Product"
                          >
                            <Icon name="Pencil" size={14} />
                          </button>
                          <button
                            onClick={() => handleDeleteProduct(product.id)}
                            className="text-error-600 hover:text-error-900 p-1.5 rounded hover:bg-error-50"
                            title="Delete Product"
                          >
                            <Icon name="Trash2" size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded Variants */}
                    {isProductExpanded && variants.length === 0 && (
                      <tr className="bg-neutral-50">
                        <td colSpan={7} className="px-6 py-4 text-center text-sm text-neutral-600">
                          No variants found. Click the + button to add a variant.
                        </td>
                      </tr>
                    )}
                    {isProductExpanded &&
                      variants.map((variant: ProductVariantResponse) => {
                        const isVariantExpanded = expanded.variants.has(variant.id);
                        const variantStock = getVariantStock(variant.id);
                        const variantStatus = getStockStatus(variantStock);
                        const availabilityItem = availabilityMap.get(variant.id);
                        // New structure: warehouse_details contains the batches/warehouses
                        const warehouseDetails = availabilityItem ? ((availabilityItem as any).warehouse_details || []) : [];

                        return (
                          <React.Fragment key={variant.id}>
                            {/* Variant Row */}
                            <tr className="bg-neutral-50">
                              <td className="px-3 py-2" colSpan={7}>
                                <div className="flex items-center justify-between gap-4">
                                  <div className="flex items-center gap-2 pl-6 flex-1 min-w-0">
                                    <button
                                      onClick={() => toggleVariant(variant.id)}
                                      className="p-1 hover:bg-neutral-200 rounded transition-colors flex-shrink-0"
                                      disabled={warehouseDetails.length === 0}
                                    >
                                      {warehouseDetails.length > 0 ? (
                                        <Icon
                                          name={isVariantExpanded ? 'ChevronDown' : 'ChevronRight'}
                                          size={14}
                                          className="text-neutral-600"
                                        />
                                      ) : (
                                        <div className="w-3" />
                                      )}
                                    </button>
                                    <Icon name="Layers" size={14} className="text-info-600 flex-shrink-0" />
                                    <div className="flex items-center gap-3 flex-wrap min-w-0 flex-1">
                                      <div className="flex flex-col gap-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className="text-sm font-medium text-neutral-900">
                                            {variant.variant_name}
                                          </span>
                                          {variant.brand_name && (
                                            <span className="text-xs text-neutral-600">
                                              - {variant.brand_name}
                                            </span>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-3 flex-wrap text-xs text-neutral-600">
                                          {variant.sku && (
                                            <span className="font-mono">
                                              SKU: {variant.sku}
                                            </span>
                                          )}
                                          {variant.hsn_code && (
                                            <span className="font-mono">
                                              HSN: {variant.hsn_code}
                                            </span>
                                          )}
                                          {variant.gst_rate !== undefined && variant.gst_rate !== null && (
                                            <span>
                                              GST: {variant.gst_rate}%
                                            </span>
                                          )}
                                          {variant.quantity && (
                                            <span>
                                              Qty: {variant.quantity}
                                            </span>
                                          )}
                                          {variant.pack_size && (
                                            <span>
                                              Pack: {variant.pack_size}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2 flex-shrink-0">
                                        <span className="text-xs font-medium text-neutral-900 whitespace-nowrap">
                                          Stock: {variantStock}
                                        </span>
                                        <Badge variant={variantStatus.variant} size="sm">
                                          {variantStatus.label}
                                        </Badge>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        setSelectedVariantForView(variant);
                                        setIsVariantViewModalOpen(true);
                                      }}
                                      className="p-1"
                                      title="View Variant Details"
                                    >
                                      <Icon name="Eye" size={14} />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        setSelectedVariantForEdit(variant);
                                        setIsVariantEditModalOpen(true);
                                      }}
                                      className="p-1"
                                      title="Edit Variant"
                                    >
                                      <Icon name="Pencil" size={14} />
                                    </Button>
                                    <Button
                                      variant="secondary"
                                      size="sm"
                                      onClick={() => {
                                        setSelectedVariantForBatch(variant);
                                        setIsAddBatchModalOpen(true);
                                      }}
                                      className="flex-shrink-0"
                                    >
                                      <Icon name="Plus" size={12} className="mr-1" />
                                      Add Batch
                                    </Button>
                                  </div>
                                </div>
                              </td>
                            </tr>

                            {/* Expanded Warehouse Details */}
                            {isVariantExpanded &&
                              warehouseDetails.map((warehouse: any, index: number) => (
                                <tr key={`${warehouse.warehouse_id}-${index}`} className="bg-neutral-25">
                                  <td className="px-3 py-2" colSpan={7}>
                                    <div className="flex items-center justify-between pl-12">
                                      <div className="flex items-center gap-4 text-xs flex-wrap">
                                        <div className="flex items-center gap-1.5">
                                          <Icon
                                            name="Warehouse"
                                            size={12}
                                            className="text-neutral-500 flex-shrink-0"
                                          />
                                          <span className="font-medium text-neutral-700 truncate max-w-[120px]">
                                            {warehouse.warehouse_name}
                                          </span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                          <Icon
                                            name="Package"
                                            size={12}
                                            className="text-neutral-500 flex-shrink-0"
                                          />
                                          <span className="text-neutral-600 whitespace-nowrap">
                                            Available: {warehouse.quantity}
                                          </span>
                                          {warehouse.expired_quantity > 0 && (
                                            <span className="text-error-600 whitespace-nowrap">
                                              | Expired: {warehouse.expired_quantity}
                                            </span>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                          <Icon
                                            name="Calendar"
                                            size={12}
                                            className="text-neutral-500 flex-shrink-0"
                                          />
                                          <span className="text-neutral-600 whitespace-nowrap">
                                            Expires: {new Date(warehouse.earliest_expiry).toLocaleDateString()}
                                          </span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                          <Badge 
                                            variant={
                                              warehouse.expiry_status === 'expired' ? 'error' :
                                              warehouse.expiry_status === 'expiring_soon' ? 'warning' : 'success'
                                            } 
                                            size="sm"
                                          >
                                            {warehouse.expiry_status.replace('_', ' ')}
                                          </Badge>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-1 flex-shrink-0">
                                        <Button variant="ghost" size="sm" className="p-1">
                                          <Icon name="Eye" size={12} />
                                        </Button>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                          </React.Fragment>
                        );
                      })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredProducts.length === 0 && (
          <div className="text-center py-12">
            <Icon name="Package" size={48} className="text-neutral-300 mx-auto mb-4" />
            <p className="text-lg font-medium text-neutral-600">No products found</p>
            <p className="text-sm text-neutral-500 mt-1">
              {searchQuery ? 'Try adjusting your search' : 'No products in this category'}
            </p>
          </div>
        )}
      </div>
      )}

      {/* Add Product Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Add Product"
        size="lg"
      >
        <ProductForm onSuccess={handleFormSuccess} onCancel={() => setIsAddModalOpen(false)} />
      </Modal>

      {/* Edit Product Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedProduct(null);
        }}
        title="Edit Product"
        size="lg"
      >
        {selectedProduct && (
          <ProductForm
            initialData={selectedProduct}
            onSuccess={handleFormSuccess}
            onCancel={() => {
              setIsEditModalOpen(false);
              setSelectedProduct(null);
            }}
          />
        )}
      </Modal>

      {/* Add Product Variant Modal */}
      <Modal
        isOpen={isAddVariantModalOpen}
        onClose={() => {
          setIsAddVariantModalOpen(false);
          setSelectedProductForVariant(null);
        }}
        title={`Add Variant to ${selectedProductForVariant?.name || 'Product'}`}
        size="lg"
      >
        {selectedProductForVariant && (
          <ProductVariantForm
            productId={selectedProductForVariant.id}
            onSuccess={handleVariantFormSuccess}
            onCancel={() => {
              setIsAddVariantModalOpen(false);
              setSelectedProductForVariant(null);
            }}
          />
        )}
      </Modal>

      {/* View Variant Modal */}
      <VariantViewModal
        variant={selectedVariantForView}
        isOpen={isVariantViewModalOpen}
        onClose={() => {
          setIsVariantViewModalOpen(false);
          setSelectedVariantForView(null);
        }}
      />

      {/* Edit Variant Modal */}
      <Modal
        isOpen={isVariantEditModalOpen}
        onClose={() => {
          setIsVariantEditModalOpen(false);
          setSelectedVariantForEdit(null);
        }}
        title="Edit Variant"
        size="lg"
      >
        {selectedVariantForEdit && (
          <ProductVariantForm
            productId={selectedVariantForEdit.product_id}
            variantId={selectedVariantForEdit.id}
            initialData={selectedVariantForEdit}
            onSuccess={() => {
              setIsVariantEditModalOpen(false);
              setSelectedVariantForEdit(null);
              handleVariantFormSuccess();
            }}
            onCancel={() => {
              setIsVariantEditModalOpen(false);
              setSelectedVariantForEdit(null);
            }}
          />
        )}
      </Modal>

      {/* Add Batch Modal */}
      <Modal
        isOpen={isAddBatchModalOpen}
        onClose={() => {
          setIsAddBatchModalOpen(false);
          setSelectedVariantForBatch(null);
        }}
        title={`Add Batch for ${(selectedVariantForBatch as unknown as Record<string, unknown>)?.product_name || 'Variant'} - ${selectedVariantForBatch?.brand_name || ''}`}
        size="lg"
      >
        {selectedVariantForBatch && (
          <BatchForm
            variantId={selectedVariantForBatch.id}
            variantName={`${(selectedVariantForBatch as unknown as Record<string, unknown>).product_name || 'Variant'} - ${selectedVariantForBatch.brand_name} (SKU: ${(selectedVariantForBatch as unknown as Record<string, unknown>).product_sku || 'N/A'})`}
            onSuccess={handleBatchFormSuccess}
            onCancel={() => {
              setIsAddBatchModalOpen(false);
              setSelectedVariantForBatch(null);
            }}
          />
        )}
      </Modal>
    </div>
  );
}
